import SaveSeatsPromptModal from '~/components/fragments/dialogs/save-seats-prompt.vue';
import { pendingSeatSessions } from '#shared/utilities/schedule/pending-seats';

// The return-visit prompt (Figma 4669:57041), counterpart to useSeatReminder:
// that one fires while Save a Seat is still closed, this one the first time the
// attendee comes back after it opens.
//
// "Seen" is localStorage for now, per Jack (2026-08-18) — deliberately easy to
// clear while testing. Moves to D1 later, which is why the key is read and
// written through two small functions rather than inline.
const SEEN_KEY = 'unbound:seat-prompt-seen';

const hasSeen = () => {
    try {
        return window.localStorage.getItem(SEEN_KEY) === '1';
    } catch {
        // Private mode / storage disabled: treat as seen so a browser that
        // cannot remember the dismissal doesn't re-prompt on every page.
        return true;
    }
};

const markSeen = () => {
    try {
        window.localStorage.setItem(SEEN_KEY, '1');
    } catch { /* nothing to do — see hasSeen */ }
};

export const useSeatPrompt = () => {
    const { loggedIn, user } = useUserSession();
    const { showSeatReservations } = useScheduleControls();
    const { open } = useModal();

    /**
     * Show the prompt once, if there is anything to prompt about.
     *
     * @param sessions  the catalog for the current surface
     * @param onSaveSeats  invoked when the attendee presses Save Seats
     */
    const maybeShowOnReturn = async (sessions, onSaveSeats) => {
        if (!import.meta.client) return false;
        if (!loggedIn.value) return false;
        // Nothing to claim until reservations are actually open.
        if (!showSeatReservations.value) return false;
        if (hasSeen()) return false;

        const pending = pendingSeatSessions(sessions, user.value);
        if (pending.length === 0) return false;

        // Marked BEFORE awaiting the modal: this is a once-ever nudge, and a
        // reload while it is open should not queue a second one.
        markSeen();

        const result = await open(SaveSeatsPromptModal, { sessions: pending });
        if (result?.saveSeats) await onSaveSeats?.(pending);
        return true;
    };

    // Test hatch — clears the flag so the prompt can be replayed without
    // digging through devtools.
    const resetSeen = () => {
        try {
            window.localStorage.removeItem(SEEN_KEY);
        } catch { /* nothing to do */ }
    };

    return { maybeShowOnReturn, resetSeen };
};
